import { color as d3Color } from 'd3-color'
import { Device, Framebuffer } from '@luma.gl/core'
import { WebGLDevice } from '@luma.gl/webgl'
import DOMPurify, { type Config as DOMPurifyConfig } from 'dompurify'

import { MAX_POINT_SIZE } from '@/graph/modules/Store'

export const isFunction = <T>(a: T): boolean => typeof a === 'function'
export const isArray = <T>(a: unknown | T[]): a is T[] => Array.isArray(a)
export const isObject = <T>(a: T): boolean => (a instanceof Object)
export const isAClassInstance = <T>(a: T): boolean => {
  if (a instanceof Object) {
    // eslint-disable-next-line @typescript-eslint/ban-types
    return (a as T & Object).constructor.name !== 'Function' && (a as T & Object).constructor.name !== 'Object'
  } else return false
}
export const isPlainObject = <T>(a: T): boolean => isObject(a) && !isArray(a) && !isFunction(a) && !isAClassInstance(a)

export function getRgbaColor (value: string | [number, number, number, number]): [number, number, number, number] {
  let rgba: [number, number, number, number]
  if (isArray(value)) {
    rgba = value
  } else {
    const color = d3Color(value)
    const rgb = color?.rgb()
    rgba = [
      (rgb?.r ?? 0) / 255,
      (rgb?.g ?? 0) / 255,
      (rgb?.b ?? 0) / 255,
      color?.opacity ?? 1,
    ]
  }

  return rgba
}

export function rgbToBrightness (r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * TODO: Migrate from deprecated `readPixelsToArrayWebGL` to CommandEncoder API
 *
 * `readPixelsToArrayWebGL` is deprecated in luma.gl v9. The recommended modern approach is:
 *
 * 1. Create a buffer to hold the pixel data:
 *    const buffer = device.createBuffer({
 *      byteLength: width * height * 4 * 4, // RGBA, 4 bytes per float
 *      usage: Buffer.COPY_DST | Buffer.MAP_READ
 *    });
 *
 * 2. Copy texture/framebuffer to buffer using command encoder:
 *    const commandEncoder = device.createCommandEncoder();
 *    commandEncoder.copyTextureToBuffer({
 *      sourceTexture: fbo, // Can be Texture or Framebuffer
 *      width: sourceWidth ?? fbo.width,
 *      height: sourceHeight ?? fbo.height,
 *      origin: [sourceX, sourceY],
 *      destinationBuffer: buffer
 *    });
 *    const commandBuffer = commandEncoder.finish();
 *    device.submit(commandBuffer);
 *
 * 3. Read the data from the buffer (async):
 *    const pixelData = await buffer.readAsync(); // Returns ArrayBuffer
 *    return new Float32Array(pixelData);
 *
 * Note: The modern approach is asynchronous, so this function signature would need to change
 * to return Promise<Float32Array> or we'd need to handle async at all call sites (18 locations).
 *
 * Migration impact:
 * - This function is used in 18 places across the codebase
 * - All call sites would need to be updated to handle async
 * - Consider batching the migration to avoid inconsistencies
 *
 * Current status: Deprecated but still functional. Keeping for now until full migration can be planned.
 *
 * @note Cosmos currently supports WebGL only; support for other device types will be added later.
 */
export function readPixels (device: Device, fbo: Framebuffer, sourceX = 0, sourceY = 0, sourceWidth?: number, sourceHeight?: number): Float32Array {
  // Let luma.gl auto-allocate based on texture format
  // It will use Float32Array for rgba32float textures
  return device.readPixelsToArrayWebGL(fbo, {
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
  }) as Float32Array
}

/**
 * Generates a short random id. Used to namespace the listeners each `Graph`
 * instance registers on shared globals like `document`, so instances never
 * replace or remove each other's handlers.
 */
export function generateRandomId (): string {
  const words = new Uint32Array(2)
  crypto.getRandomValues(words)
  // Each word is padded to the 7 base-36 digits a full uint32 needs, so the
  // boundary between them is fixed: with variable widths two different pairs
  // can concatenate to the same id.
  return Array.from(words, (word) => word.toString(36).padStart(7, '0')).join('')
}

/**
 * Extracts point indices from a pixel readback buffer.
 * Every 4th value (R channel) is checked — non-zero means the point at that index was found.
 *
 * @param pointsNumber Number of real points. The texture is square, so the texels
 * past this count are padding holding position `(0, 0)`; they match any search area
 * covering the space origin and would be reported as points that do not exist.
 * Omit it to read the whole buffer.
 */
export function extractIndicesFromPixels (pixels: Float32Array, pointsNumber?: number): number[] {
  const result: number[] = []
  const count = Math.min(pixels.length / 4, pointsNumber ?? Infinity)
  for (let i = 0; i < count; i += 1) {
    if (pixels[i * 4] !== 0) result.push(i)
  }
  return result
}

/**
 * Returns the maximum point size supported by the device, scaled by pixel ratio.
 * For WebGL devices, reads the limit from the context; for other device types, uses MAX_POINT_SIZE from Store.
 * @param device - The luma.gl device
 * @param pixelRatio - Device pixel ratio to scale the result
 * @returns Maximum point size (device limit / pixelRatio)
 */
export function getMaxPointSize (device: Device, pixelRatio: number): number {
  switch (device.info.type) {
  case 'webgl': {
    const gl = (device as WebGLDevice).gl
    const range = gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE) as [number, number]
    return (range?.[1] ?? MAX_POINT_SIZE) / pixelRatio
  }
  case 'webgpu':
    // Will be implemented when WebGPU support is added
    return MAX_POINT_SIZE / pixelRatio
  default:
    return MAX_POINT_SIZE / pixelRatio
  }
}

export function clamp (num: number, min: number, max: number): number {
  return Math.min(Math.max(num, min), max)
}

export function isNumber (value: number | undefined | null | typeof NaN): boolean {
  return value !== undefined && value !== null && !Number.isNaN(value)
}

/**
 * Reports whether the point at `index` is absent (removed): its position is `NaN`.
 * A point is absent when either coordinate is `NaN`.
 */
export function isPointAbsent (pointPositions: Float32Array, index: number): boolean {
  return Number.isNaN(pointPositions[index * 2] as number) || Number.isNaN(pointPositions[index * 2 + 1] as number)
}

/**
 * Sanitizes HTML content to prevent XSS attacks using DOMPurify
 *
 * This function is used internally to sanitize HTML content before setting innerHTML,
 * such as in attribution text. It uses a safe default configuration that allows
 * only common safe HTML elements and attributes.
 *
 * @param html The HTML string to sanitize
 * @param options Optional DOMPurify configuration options to override defaults
 * @returns Sanitized HTML string safe for innerHTML usage
 */
export function sanitizeHtml (html: string, options?: DOMPurifyConfig): string {
  return DOMPurify.sanitize(html, {
    // Default configuration: allow common safe HTML elements and attributes
    ALLOWED_TAGS: ['a', 'b', 'i', 'em', 'strong', 'span', 'div', 'p', 'br'],
    ALLOWED_ATTR: ['href', 'target', 'class', 'id', 'style'],
    ALLOW_DATA_ATTR: false,
    ...options,
  })
}
